"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, TrendingUp, MapPin, Target, ChartBarBig as BarChart3, ArrowRight, CircleCheck as CheckCircle2, Compass, ShieldCheck, Building2, Sparkles, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const searchTrendsData = [{
  category: "Commercial & B2B Services",
  growth: "+42%",
  insight: "High commercial intent searches for localized consultancy in Lincoln and Greater Lincolnshire."
}, {
  category: "Mobile & Near Me Queries",
  growth: "+68%",
  insight: "Mobile searches seeking immediate local business expertise and specialized digital services."
}, {
  category: "Map Pack Local Intent",
  growth: "+55%",
  insight: "Google Maps impressions driving over 60% of high-converting inbound leads for local experts."
}];
const keywordAnalysis = [{
  keyword: "SEO consultant Lincoln",
  intent: "Transactional / High-Intent",
  volume: "High Local Demand",
  difficulty: "Moderate",
  opportunity: "High organic growth potential with tailored landing pages and structured schema."
}, {
  keyword: "B2B digital marketing Lincoln",
  intent: "Commercial Research",
  volume: "Medium Steady",
  difficulty: "Low-Moderate",
  opportunity: "Ideal candidate for thought-leadership content and direct consultant pitch."
}, {
  keyword: "SEO consultancy services Lincolnshire",
  intent: "High-Value Commercial",
  volume: "Regional Target",
  difficulty: "Low",
  opportunity: "Unbounded regional reach covering Lincoln, Newark, and surrounding industrial hubs."
}, {
  keyword: "Local SEO strategy Lincoln",
  intent: "Problem-Solving",
  volume: "Medium-High",
  difficulty: "Moderate",
  opportunity: "Pairs with Google Business Profile optimization and local citation architecture."
}];
const competitorInsights = [{
  title: "Generic Agency Templates vs tailored strategies",
  description: "Most agencies in the East Midlands apply generic, automated checklist SEO. I deliver bespoke strategies mapped directly to Lincoln's distinct commercial landscape."
}, {
  title: "Thin Local Landing Pages",
  description: "Competitors frequently build doorway pages without local authority. My approach combines deep Lincolnshire market context with technical precision and genuine authority."
}, {
  title: "Neglected Map Pack Optimization",
  description: "Local competitors overlook geo-targeted citations and entity matching, leaving prime Google Maps positions accessible for targeted campaign pushes."
}];
const opportunities = [{
  icon: Target,
  title: "Lincoln Local Entity Building",
  description: "Establish strong local search entities connecting your business with Lincoln landmarks, local business registries, and Lincolnshire contextual relevance."
}, {
  icon: BarChart3,
  title: "Hyper-Targeted B2B Lead Conversion",
  description: "Capture decision-makers searching across Lincoln and nearby commercial corridors with intent-focused landing experiences and strategic technical SEO."
}, {
  icon: Sparkles,
  title: "AI Search & SGE Readiness",
  description: "Prepare your brand for modern AI search engines like ChatGPT Search and Google Gemini, ensuring your business is recommended for local queries."
}];
const neighboringCities = [{
  name: "Hull",
  distance: "45 miles",
  path: "/LocalSeoConsultantHullPage",
  description: "Maritime and industrial hub search strategies."
}, {
  name: "Peterborough",
  distance: "52 miles",
  path: "/LocalSeoConsultantPeterboroughPage",
  description: "Cambridgeshire commercial & tech market focus."
}, {
  name: "Doncaster",
  distance: "40 miles",
  path: "/LocalSeoConsultantDoncasterPage",
  description: "South Yorkshire logistics & enterprise search."
}, {
  name: "Derby",
  distance: "55 miles",
  path: "/LocalSeoConsultantDerbyPage",
  description: "East Midlands manufacturing & professional services."
}, {
  name: "Wakefield",
  distance: "58 miles",
  path: "/LocalSeoConsultantWakefieldPage",
  description: "West Yorkshire B2B and regional search growth."
}, {
  name: "Northampton",
  distance: "85 miles",
  path: "/LocalSeoConsultantNorthamptonPage",
  description: "Central logistics and B2B market optimization."
}];
export default function Lincoln() {
  const [activeKeywordTab, setActiveKeywordTab] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="lincolnlocalsearchanalysis" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden opacity-30">
        <div className="absolute -top-24 left-1/2 -z-10 h-96 w-96 -translate-x-1/2 rounded-full bg-secondary/60 blur-3xl" />
        <div className="absolute top-1/2 right-0 -z-10 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={containerVariants} className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1.5 text-xs font-semibold text-foreground">
            <Compass className="size-3.5 text-primary" />
            <span>Lincoln & Greater Lincolnshire Search Analysis</span>
          </div>

          <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Local Search Trends & Strategy for Lincoln Businesses
          </h2>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            I provide hands-on, bespoke SEO analysis and strategic execution for Lincolnshire enterprises. Discover how targeted local optimization drives sustainable, high-converting organic traffic directly to your business.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={containerVariants} className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {searchTrendsData.map((trend, i) => <div data-index={i} key={trend.category}>
              <motion.div variants={itemVariants} className="h-full">
                <Card className="relative h-full overflow-hidden border-border bg-card shadow-sm transition-all duration-300 hover:border-primary/40 hover:shadow-md">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary" className="bg-secondary text-foreground">
                        Search Growth
                      </Badge>
                      <span className="flex items-center gap-1 font-mono text-lg font-bold text-primary">
                        <TrendingUp className="size-4" />
                        {trend.growth}
                      </span>
                    </div>
                    <CardTitle className="mt-3 text-lg font-semibold text-foreground">
                      {trend.category}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{trend.insight}</p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>)}
        </motion.div>

        <div className="mt-16 rounded-2xl border border-border bg-card p-6 shadow-sm md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <Search className="size-4" />
                <span>Lincoln High-Intent Keywords</span>
              </div>
              <h2 className="mt-1 font-serif text-2xl font-bold text-foreground sm:text-3xl">
                Lincoln Local Keyword Opportunities
              </h2>
            </div>
            <p className="max-w-md text-sm text-muted-foreground">
              Direct consultant research targeting buyer intent across Lincoln city center, Hykeham, Navenby, and surrounding commercial areas.
            </p>
          </div>

          <Separator className="my-6 bg-border" />

          <div className="grid gap-6 lg:grid-cols-12">
            <div className="space-y-3 lg:col-span-5">
              {keywordAnalysis.map((item, idx) => <div data-index={idx} key={item.keyword}>
                  <Button variant={activeKeywordTab === idx ? "default" : "outline"} className={`w-full justify-start text-left h-auto py-3 px-4 font-medium transition-all ${activeKeywordTab === idx ? "bg-primary text-primary-foreground shadow-sm" : "bg-background text-foreground hover:bg-muted"}`} onClick={() => setActiveKeywordTab(idx)}>
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="truncate">{item.keyword}</span>
                      <Badge variant={activeKeywordTab === idx ? "secondary" : "outline"} className={activeKeywordTab === idx ? "bg-primary-foreground/20 text-primary-foreground border-transparent" : "text-muted-foreground"}>
                        {item.difficulty}
                      </Badge>
                    </div>
                  </Button>
                </div>)}
            </div>

            <div className="rounded-xl border border-border bg-background p-6 lg:col-span-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="text-xs font-mono uppercase text-muted-foreground">Focus Query</span>
                  <h3 className="text-xl font-bold text-foreground">
                    {keywordAnalysis[activeKeywordTab].keyword}
                  </h3>
                </div>
                <Badge variant="secondary" className="bg-secondary text-foreground">
                  {keywordAnalysis[activeKeywordTab].intent}
                </Badge>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4 border-y border-border py-4 text-sm">
                <div>
                  <span className="block text-xs text-muted-foreground">Search Volume</span>
                  <span className="font-semibold text-foreground">
                    {keywordAnalysis[activeKeywordTab].volume}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-muted-foreground">SEO Difficulty</span>
                  <span className="font-semibold text-foreground">
                    {keywordAnalysis[activeKeywordTab].difficulty}
                  </span>
                </div>
              </div>

              <div className="mt-4">
                <span className="text-xs font-medium text-muted-foreground">Strategic Insight & Execution</span>
                <p className="mt-1 text-sm font-medium leading-relaxed text-foreground">
                  {keywordAnalysis[activeKeywordTab].opportunity}
                </p>
              </div>

              <div className="mt-6 flex items-center gap-2 text-xs font-semibold text-primary">
                <CheckCircle2 className="size-4" />
                <span>Working directly with Yassine guarantees custom landing page execution.</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 grid gap-8 lg:grid-cols-2">
          <div className="flex flex-col justify-between rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <div>
              <div className="inline-flex items-center gap-2 rounded-md bg-secondary px-2.5 py-1 text-xs font-medium text-foreground">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>Local Competition Analysis</span>
              </div>

              <h2 className="mt-4 font-serif text-2xl font-bold text-foreground sm:text-3xl">
                Outperforming Lincoln Local Competitors
              </h2>

              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                Many local businesses rely on outdated agency packages that focus on vanity metrics. My approach isolates real search demand, fixing technical bottlenecks and building genuine Lincolnshire domain authority.
              </p>

              <div className="mt-6 space-y-4">
                {competitorInsights.map((item, i) => <div data-index={i} key={item.title} className="flex items-start gap-3">
                    <div className="mt-1 rounded-full bg-primary/10 p-1 text-primary">
                      <Zap className="size-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">{item.title}</h3>
                      <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="mt-8 border-t border-border pt-6">
              <p className="text-xs text-muted-foreground">
                You work directly with me—Yassine Essaadi. No account managers or junior pass-offs.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="font-serif text-2xl font-bold text-foreground sm:text-3xl">
              High-Impact Lincoln SEO Opportunities
            </h2>
            <p className="text-sm text-muted-foreground">
              Immediate strategy priorities designed to secure Map Pack dominance and top-tier organic visibility across Lincoln.
            </p>

            <div className="space-y-4 pt-2">
              {opportunities.map((opp, idx) => {
              const IconComponent = opp.icon;
              return <div data-index={idx} key={opp.title}>
                    <Card className="border-border bg-card shadow-xs transition-all duration-300 hover:border-primary/40 hover:shadow-sm">
                      <CardHeader className="p-5">
                        <div className="flex items-start gap-4">
                          <div className="rounded-lg bg-secondary p-2.5 text-primary shrink-0">
                            <IconComponent className="size-5" />
                          </div>
                          <div>
                            <CardTitle className="text-base font-semibold text-foreground">
                              {opp.title}
                            </CardTitle>
                            <CardDescription className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
                              {opp.description}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  </div>;
            })}
            </div>
          </div>
        </div>

        <div className="mt-16 rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <MapPin className="size-4" />
                <span>Regional Search Coverage</span>
              </div>
              <h2 className="mt-1 font-serif text-2xl font-bold text-foreground">
                Neighboring City & Regional SEO Expertise
              </h2>
            </div>
            <p className="text-xs text-muted-foreground sm:max-w-xs sm:text-right">
              Expanding your search footprint across key East Midlands and Yorkshire business locations.
            </p>
          </div>

          <Separator className="my-6 bg-border" />

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {neighboringCities.map((city, index) => <div data-index={index} key={city.name}>
                <Link to={city.path} className="group flex flex-col justify-between rounded-xl border border-border bg-background p-4 transition-all duration-200 hover:border-primary/50 hover:bg-muted/50 hover:shadow-xs">
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        Local SEO {city.name}
                      </span>
                      <Badge variant="outline" className="text-[12px] text-muted-foreground">
                        {city.distance}
                      </Badge>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground line-clamp-2">
                      {city.description}
                    </p>
                  </div>

                  <div className="mt-4 flex items-center gap-1 text-xs font-medium text-primary">
                    <span>View City Strategy</span>
                    <ArrowRight className="size-3 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              </div>)}
          </div>
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={containerVariants} className="mt-16 overflow-hidden rounded-2xl bg-primary text-primary-foreground p-8 text-center shadow-lg md:p-12">
          <div className="mx-auto max-w-2xl">
            <Badge variant="secondary" className="bg-primary-foreground/15 text-primary-foreground border-transparent">
              Direct Consultant Access
            </Badge>

            <h2 className="mt-4 font-serif text-xl font-bold tracking-tight text-primary-foreground sm:text-2xl lg:text-3xl">
              Ready to Expand Your Lincoln Search Visibility?
            </h2>

            <p className="mt-4 text-sm leading-relaxed text-primary-foreground/90 sm:text-base">
              Book a free local SEO consultation directly with me. I will review your website, analyze your Lincoln competitors, and construct a bespoke growth strategy.
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto font-semibold text-foreground hover:bg-secondary/90">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                </Link>
              </Button>

              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>

            <p className="mt-6 text-xs text-primary-foreground/80">
              No long-term contracts • You work directly with Yassine Essaadi
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}